import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as awarenessProtocol from 'y-protocols/awareness';

interface CursorData {
  clientId: number;
  name: string;
  color: string;
  x: number;
  y: number;
}

interface CollaborativeCursorsProps {
  awareness: awarenessProtocol.Awareness;
  containerRef: React.RefObject<HTMLDivElement | null>;
  stageRef?: React.RefObject<any>;
}

const CURSOR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#FD79A8', '#FDCB6E', '#6C5CE7'
];

export const CollaborativeCursors: React.FC<CollaborativeCursorsProps> = ({
  awareness,
  containerRef,
  stageRef
}) => {
  const [cursors, setCursors] = useState<Map<number, CursorData>>(new Map());
  const localClientId = awareness.clientID;

  // Track local mouse movement
  const lastSentRef = useRef<number>(0);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    // If using Konva stage, transform to stage coordinates
    if (stageRef?.current) {
      const stage = stageRef.current;
      const transform = stage.getAbsoluteTransform().copy().invert();
      const pos = transform.point({ x, y });
      x = pos.x;
      y = pos.y;
    }

    // Throttle to ~30fps and avoid tiny movements
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - lastSentRef.current < 33) return;
    const prev = lastPosRef.current;
    const dx = prev ? x - prev.x : 999;
    const dy = prev ? y - prev.y : 999;
    if (dx * dx + dy * dy < 0.25) return;
    lastSentRef.current = now;
    lastPosRef.current = { x, y };

    awareness.setLocalStateField('cursor', { x, y });
  }, [awareness, containerRef, stageRef]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    awareness.setLocalStateField('cursor', null);
  }, [awareness]);

  // Set up mouse tracking
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let rafId: number | null = null;

    const attachListeners = (element: HTMLDivElement) => {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);

      cleanup = () => {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    const ensureContainerReady = () => {
      const element = containerRef.current;
      if (!element) {
        rafId = requestAnimationFrame(ensureContainerReady);
        return;
      }
      attachListeners(element);
    };

    ensureContainerReady();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [containerRef, handleMouseMove, handleMouseLeave]);

  // Listen for awareness updates, but only re-render when cursor data actually changes
  useEffect(() => {
    const prevRef = { current: new Map<number, CursorData>() } as { current: Map<number, CursorData> };

    const computeMap = () => {
      const states = awareness.getStates();
      const map = new Map<number, CursorData>();

      states.forEach((state, clientId) => {
        // Skip our own cursor
        if (clientId === localClientId) {
          return;
        }

        const cursor = state.cursor as { x: number; y: number } | null;
        const user = state.user as { name: string; color: string } | undefined;

        if (cursor) {
          // Even if user data is missing, show the cursor with a fallback name
          map.set(clientId, {
            clientId,
            name: user?.name || `User ${clientId}`,
            color: user?.color || CURSOR_COLORS[clientId % CURSOR_COLORS.length],
            x: cursor.x,
            y: cursor.y,
          });
        }
      });

      return map;
    };

    const mapsEqual = (a: Map<number, CursorData>, b: Map<number, CursorData>) => {
      if (a.size !== b.size) return false;
      for (const [k, v] of a) {
        const w = b.get(k);
        if (!w || v.x !== w.x || v.y !== w.y || v.name !== w.name || v.color !== w.color) return false;
      }
      return true;
    };

    const onUpdate = () => {
      const next = computeMap();
      if (!mapsEqual(next, prevRef.current)) {
        prevRef.current = next;
        setCursors(next);
      }
    };

    awareness.on('update', onUpdate);
    onUpdate();
    return () => {
      awareness.off('update', onUpdate);
    };
  }, [awareness, localClientId]);

  // Transform cursor position if using Konva stage
  const transformPosition = (x: number, y: number) => {
    if (!stageRef?.current) return { x, y };

    const stage = stageRef.current;
    const transform = stage.getAbsoluteTransform();
    const pos = transform.point({ x, y });
    return pos;
  };

  return (
    <div className="pointer-events-none absolute inset-0 z-50">
      {Array.from(cursors.values()).map((cursor) => {
        const pos = transformPosition(cursor.x, cursor.y);
        return (
          <div
            key={cursor.clientId}
            className="absolute transition-all duration-100 ease-out"
            style={{
              left: `${pos.x}px`,
              top: `${pos.y}px`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            {/* Cursor pointer */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="drop-shadow-md"
              style={{ fill: cursor.color }}
            >
              <path d="M5.5 3.21V20.8l6.248-6.248H18.5L5.5 3.21z" />
            </svg>
            
            {/* User name label */}
            <div
              className="absolute left-6 top-0 px-2 py-1 rounded text-xs font-medium text-white whitespace-nowrap"
              style={{
                backgroundColor: cursor.color,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {cursor.name}
            </div>
          </div>
        );
      })}
    </div>
  );
};
